import React, { useState, useEffect } from 'react';
import {
  getFragranceCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getFragrances,
  createFragrance,
  updateFragrance,
  deleteFragrance,
  toggleFragranceAvailability
} from '../../api/adminApi';
import Modal from '../../components/admin/Modal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';

function FragrancesPage() {
  const [activeTab, setActiveTab] = useState('fragrances');
  const [categories, setCategories] = useState([]);
  const [fragrances, setFragrances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, item: null, type: '' });

  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesData, fragrancesData] = await Promise.all([
        getFragranceCategories(),
        getFragrances()
      ]);
      setCategories(categoriesData);
      setFragrances(fragrancesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const openCreateFragranceModal = () => {
    setEditingItem(null);
    setFormData({
      category_id: categories[0]?.id || '',
      name: '',
      display_order: 0,
      is_available: true
    });
    setIsModalOpen(true);
  };

  const openEditFragranceModal = (fragrance) => {
    setEditingItem({ ...fragrance, type: 'fragrance' });
    setFormData({
      category_id: fragrance.category_id,
      name: fragrance.name,
      display_order: fragrance.display_order || 0,
      is_available: fragrance.is_available
    });
    setIsModalOpen(true);
  };

  const openCreateCategoryModal = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      color: '#9c6f44',
      display_order: categories.length
    });
    setIsModalOpen(true);
  };

  const openEditCategoryModal = (category) => {
    setEditingItem({ ...category, type: 'category' });
    setFormData({
      name: category.name,
      color: category.color || '#9c6f44',
      display_order: category.display_order || 0
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const isCategory = activeTab === 'categories' || editingItem?.type === 'category';

      if (isCategory) {
        if (editingItem) {
          await updateCategory(editingItem.id, formData);
        } else {
          await createCategory(formData);
        }
      } else {
        if (editingItem) {
          await updateFragrance(editingItem.id, formData);
        } else {
          await createFragrance(formData);
        }
      }

      await fetchData();
      closeModal();
    } catch (err) {
      console.error('Error saving:', err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (item, type) => {
    setDeleteDialog({ isOpen: true, item, type });
  };

  const handleDeleteConfirm = async () => {
    try {
      if (deleteDialog.type === 'category') {
        await deleteCategory(deleteDialog.item.id);
        setCategories(categories.filter(c => c.id !== deleteDialog.item.id));
      } else {
        await deleteFragrance(deleteDialog.item.fragrance_id);
        await fetchData();
      }
      setDeleteDialog({ isOpen: false, item: null, type: '' });
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleToggleAvailability = async (fragranceId) => {
    try {
      await toggleFragranceAvailability(fragranceId);
      await fetchData();
    } catch (err) {
      console.error('Error toggling availability:', err);
      alert('Erreur lors du changement de disponibilité');
    }
  };

  const flatFragrances = fragrances.flatMap(cat =>
    (cat.fragrances || []).map(f => ({
      ...f,
      category_id: cat.id,
      category_name: cat.name,
      fragrance_id: f.id
    }))
  );

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <div>
      <h1 style={{ marginBottom: '2rem', color: '#2c3e50' }}>Gestion des Senteurs</h1>

      {/* Tabs */}
      <div style={{ marginBottom: '2rem', borderBottom: '2px solid #dee2e6' }}>
        <button
          onClick={() => setActiveTab('fragrances')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: activeTab === 'fragrances' ? '#9c6f44' : 'transparent',
            color: activeTab === 'fragrances' ? 'white' : '#7f8c8d',
            border: 'none',
            borderBottom: activeTab === 'fragrances' ? '2px solid #9c6f44' : 'none',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Senteurs ({flatFragrances.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: activeTab === 'categories' ? '#9c6f44' : 'transparent',
            color: activeTab === 'categories' ? 'white' : '#7f8c8d',
            border: 'none',
            borderBottom: activeTab === 'categories' ? '2px solid #9c6f44' : 'none',
            cursor: 'pointer',
            fontWeight: 600,
            marginLeft: '0.5rem'
          }}
        >
          Catégories ({categories.length})
        </button>
      </div>

      {/* Fragrances Tab */}
      {activeTab === 'fragrances' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ color: '#2c3e50', fontSize: '1.5rem' }}>Senteurs</h2>
            <button
              onClick={openCreateFragranceModal}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#9c6f44',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              + Nouvelle senteur
            </button>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Nom</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Catégorie</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Disponible</th>
                  <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flatFragrances.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                      Aucune senteur trouvée
                    </td>
                  </tr>
                ) : (
                  flatFragrances.map(fragrance => (
                    <tr key={fragrance.fragrance_id} style={{
                      borderBottom: '1px solid #dee2e6',
                      opacity: fragrance.is_available ? 1 : 0.6,
                      backgroundColor: fragrance.is_available ? 'transparent' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '1rem', fontWeight: 500 }}>{fragrance.name}</td>
                      <td style={{ padding: '1rem', color: '#7f8c8d' }}>{fragrance.category_name}</td>
                      <td style={{ padding: '1rem' }}>
                        <button
                          onClick={() => handleToggleAvailability(fragrance.fragrance_id)}
                          style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: fragrance.is_available ? '#d4edda' : '#f8d7da',
                            color: fragrance.is_available ? '#155724' : '#721c24',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            fontWeight: 500,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                          {fragrance.is_available ? '✓ Disponible' : '✗ Indisponible'}
                        </button>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        <button
                          onClick={() => openEditFragranceModal(fragrance)}
                          style={{
                            padding: '0.5rem 1rem',
                            marginRight: '0.5rem',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Éditer
                        </button>
                        <button
                          onClick={() => handleDeleteClick(fragrance, 'fragrance')}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ color: '#2c3e50', fontSize: '1.5rem' }}>Catégories</h2>
            <button
              onClick={openCreateCategoryModal}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#9c6f44',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              + Nouvelle catégorie
            </button>
          </div>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Nom</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Couleur</th>
                  <th style={{ padding: '1rem', textAlign: 'left' }}>Ordre</th>
                  <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                      Aucune catégorie trouvée
                    </td>
                  </tr>
                ) : (
                  categories.map(category => (
                    <tr key={category.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '1rem', fontWeight: 500 }}>{category.name}</td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{
                            width: '30px',
                            height: '30px',
                            backgroundColor: category.color,
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                          }} />
                          <span style={{ color: '#7f8c8d' }}>{category.color}</span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>{category.display_order}</td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        <button
                          onClick={() => openEditCategoryModal(category)}
                          style={{
                            padding: '0.5rem 1rem',
                            marginRight: '0.5rem',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Éditer
                        </button>
                        <button
                          onClick={() => handleDeleteClick(category, 'category')}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal for both fragrances and categories */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={
          activeTab === 'categories' || editingItem?.type === 'category'
            ? (editingItem ? 'Modifier la catégorie' : 'Nouvelle catégorie')
            : (editingItem ? 'Modifier la senteur' : 'Nouvelle senteur')
        }
      >
        <form onSubmit={handleSubmit}>
          {(activeTab === 'fragrances' && !editingItem?.type) || editingItem?.type === 'fragrance' ? (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Catégorie *
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Nom *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_available"
                    checked={formData.is_available}
                    onChange={handleInputChange}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span>Disponible</span>
                </label>
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Nom *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Couleur *
                </label>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    height: '50px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Ordre d'affichage
                </label>
                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleInputChange}
                  min="0"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={closeModal}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#9c6f44',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, item: null, type: '' })}
        title="Confirmer la suppression"
        message={
          deleteDialog.type === 'category'
            ? `Êtes-vous sûr de vouloir supprimer la catégorie "${deleteDialog.item?.name}" ? Toutes les senteurs associées seront également supprimées.`
            : `Êtes-vous sûr de vouloir supprimer la senteur "${deleteDialog.item?.name}" ?`
        }
      />
    </div>
  );
}

export default FragrancesPage;
